import {
  useHasPreviousUnsavedChanges,
  useHasUnsavedChanges,
  useIsLastSeenItem,
  useNextItemId,
  usePreviousItemId,
} from '@gleamer/ui';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import React, { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

type ItemNavigatorProps = HTMLAttributes<HTMLButtonElement> & {
  itemId: string | null;
  hasOtherUnsavedChanges?: boolean;
};

function ItemNavigator({
  className,
  itemId,
  children,
  hasOtherUnsavedChanges,
  ...props
}: ItemNavigatorProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const hasUnsavedChanges = useHasUnsavedChanges();
  const isLastSeenItem = useIsLastSeenItem();

  const onButtonClick = () => {
    if (isLastSeenItem || !hasUnsavedChanges) {
      searchParams.set('item', itemId);
      return setSearchParams(searchParams);
    }

    const confirm = window.confirm(
      'You have unsaved changes. Are you sure you want to see another case?'
    );

    if (confirm) {
      searchParams.set('item', itemId);
      return setSearchParams(searchParams);
    }
  };

  if (!itemId) {
    return (
      <div
        aria-disabled="true"
        className={classNames('inline-flex h-9 w-9 items-center justify-center rounded-[9px] text-dl-faint opacity-50', className)}
      >
        {children}
      </div>
    );
  }

  return (
    <button
      onClick={onButtonClick}
      className={classNames(
        'relative inline-flex h-9 w-9 items-center justify-center rounded-[9px] text-dl-muted transition-colors hover:bg-dl-sunken hover:text-dl-text focus:outline-none focus-visible:ring-2 focus-visible:ring-dl-accent',
        className
      )}
      {...props}
    >
      {children}
      {hasOtherUnsavedChanges && (
        <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-dl-warning" aria-label="unsaved changes" />
      )}
    </button>
  );
}

export function PreviousItemButtons() {
  const { t } = useTranslation();

  const previousItemId = usePreviousItemId();
  const nextItemId = useNextItemId();
  const hasPreviousUnsavedChanges = useHasPreviousUnsavedChanges();

  if (!previousItemId && !nextItemId) {
    return null;
  }

  return (
    <div className="mr-2 flex items-center gap-0.5">
      <ItemNavigator
        itemId={previousItemId}
        title={t('Previous case')}
        hasOtherUnsavedChanges={hasPreviousUnsavedChanges}
      >
        <ChevronLeftIcon className="h-[18px] w-[18px]" />
      </ItemNavigator>
      <ItemNavigator itemId={nextItemId} title={t('Next case')}>
        <ChevronRightIcon className="h-[18px] w-[18px]" />
      </ItemNavigator>
    </div>
  );
}
